import type { V2Balance, V2Token } from '../../../types/v2';
import { compareByNetwork } from '../../../utils/networkOrder';
import { COUNTER_SYMBOL, isCompatiblePair } from './exchangeQuote';
import type { SellOption } from './sellOptions';

/**
 * Form values filled in on open — the Buy/Sell buttons of the stock and
 * crypto token screens. `id` changes per request, so the same token can be
 * pre-filled again later.
 */
export interface ExchangePreset {
  id: number;
  /** Sell from the user's balance in this token row (stocks: one row per stock). */
  sellTokenId?: string;
  /**
   * Sell from the user's balance with this symbol (crypto: the catalogue has
   * several rows per ticker, the holding may sit on any of them). The
   * balance with the most available is taken.
   */
  sellSymbol?: string;
  /** Buy this token row. */
  buyTokenId?: string;
  /** Buy a token with this symbol (first by network order), e.g. "USDT". */
  buySymbol?: string;
  /** Limit price, 1 Buy token in Sell tokens. */
  price?: string;
}

export interface ResolvedPreset {
  /** New Sell balance; undefined keeps the current one. */
  sell?: V2Balance | null;
  /** New Buy token; undefined keeps the current one. */
  buy?: V2Token;
  /** The preset asked to sell a token the user does not hold: its symbol, or "" when unknown. */
  notHeld?: string;
}

const byAvailableDesc = (a: SellOption, b: SellOption) => b.available - a.available;

/**
 * Works out what a preset selects, given the user's sell options and the
 * Buy catalogue. Pair rules (3.6) are respected: when the preset names only
 * the Buy side, Sell becomes a compatible balance of another token — USDT
 * with the most available when there is one.
 */
export const resolveExchangePreset = (
  preset: ExchangePreset,
  sellOptions: SellOption[],
  tokens: V2Token[],
  currentSell: V2Balance | null,
  isStock: (symbol: string) => boolean,
): ResolvedPreset => {
  const result: ResolvedPreset = {};
  const held = (o: SellOption) => o.available > 0;

  if (preset.sellTokenId || preset.sellSymbol) {
    const holding = preset.sellTokenId
      ? sellOptions.find((o) => o.balance.token_id === preset.sellTokenId && held(o))
      : sellOptions.filter((o) => o.symbol === preset.sellSymbol && held(o)).sort(byAvailableDesc)[0];
    if (holding) {
      result.sell = holding.balance;
    } else {
      result.notHeld = preset.sellSymbol
        ?? tokens.find((t) => t.id === preset.sellTokenId)?.symbol
        ?? '';
      if (!currentSell && sellOptions.length > 0) result.sell = sellOptions[0].balance;
    }
  }

  const buy = preset.buyTokenId
    ? tokens.find((t) => t.id === preset.buyTokenId)
    : preset.buySymbol
      ? tokens
          .filter((t) => t.symbol === preset.buySymbol)
          .sort((a, b) => compareByNetwork({ networkSymbol: a.network_symbol }, { networkSymbol: b.network_symbol }))[0]
      : undefined;
  if (!buy) return result;

  const sellSymbol = (result.sell ?? currentSell)?.token_symbol;
  if (preset.sellTokenId || preset.sellSymbol) {
    // Selling a token for itself is not an exchange: leave Buy for the user.
    if (sellSymbol !== buy.symbol) result.buy = buy;
    return result;
  }

  result.buy = buy;
  const fits = (o: SellOption) => o.symbol !== buy.symbol && isCompatiblePair(o.symbol, buy.symbol, isStock);
  const usdt = sellOptions
    .filter((o) => o.symbol.toUpperCase() === COUNTER_SYMBOL && held(o) && fits(o))
    .sort(byAvailableDesc)[0];
  const currentFits = currentSell
    && currentSell.token_symbol !== buy.symbol
    && isCompatiblePair(currentSell.token_symbol, buy.symbol, isStock);
  if (usdt) {
    result.sell = usdt.balance;
  } else if (!currentFits) {
    // A balance with something in it first; an empty one only when there is
    // nothing else, so the field names the account that needs funding
    // rather than going blank.
    result.sell = (sellOptions.find((o) => fits(o) && held(o)) ?? sellOptions.find(fits))?.balance ?? null;
  }
  return result;
};

/**
 * What Exchange should open on when it is reached from an account screen.
 *
 * The Exchange form never sees the account the client came from — Operations
 * hands it a preset and nothing else — so without one it fell back to the
 * first row of the sell list, which is USDT-TRC20. Opening Exchange from a
 * DOGE account and being shown USDT is the bug; from the Dashboard, where
 * there is no account to speak of, that same first row is exactly right, so
 * that path still passes null.
 *
 * A real per-network balance is named by its token id, which pins the row
 * and the chain the client was looking at. The Dashboard's grouped tile is a
 * virtual aggregate whose token id matches no sell row, so it goes by symbol
 * and the resolver takes the chain holding the most.
 *
 * An account with nothing in it returns null on purpose: the resolver
 * answers a preset it cannot satisfy with a "Nothing to sell" alert, and
 * that is a poor greeting for someone who only opened the screen to look.
 */
export const presetForBalance = (
  balance: V2Balance | null | undefined,
  id: number,
): ExchangePreset | null => {
  if (!balance) { return null; }
  const available = parseFloat(balance.available_amount ?? balance.amount ?? '0') || 0;
  if (available <= 0) { return null; }
  return balance.provider_name === 'virtual'
    ? { id, sellSymbol: balance.token_symbol }
    : { id, sellTokenId: balance.token_id };
};
